/**
 * The DOM half of `docs/KNOWN-BUGS.md` item 3.
 *
 * `InputController` (see `input-controller.ts`) is the pure state machine;
 * this module is the thin, deliberately un-clever layer that turns pointer
 * events on the on-screen buttons into `press`/`release` calls on it. Every
 * button gets pointer capture and `touch-action: none`, and every press has
 * three ways to end at the element (`pointerup`, `pointercancel`,
 * `lostpointercapture`) plus a window-level catch-all — this is the exact
 * list `docs/KNOWN-BUGS.md` item 3 calls for, because item 3 is what happens
 * when one of them is missing: "a tapped direction button repeats forever;
 * press both and the piece vibrates in place."
 *
 * Releases are tracked per `pointerId` rather than as one global
 * "release everything", so holding a direction with one finger while tapping
 * rotate with another does not cancel the hold — a small improvement on the
 * reference prototype's single-finger assumption, and still exactly as safe:
 * every press still has a matching release on every one of the four paths.
 * `blur` and `visibilitychange` fall back to releasing everything, because at
 * that point the page cannot be trusted to know which pointers still exist.
 */
import type { ControllerAction, InputController } from './input-controller.js';

const CONTROLLER_ACTIONS: ReadonlySet<string> = new Set<ControllerAction>([
  'left',
  'right',
  'softDrop',
  'hardDrop',
  'rotateCW',
  'rotateCCW',
  'hold',
]);

function isControllerAction(value: string | undefined): value is ControllerAction {
  return value !== undefined && CONTROLLER_ACTIONS.has(value);
}

export interface BindTouchControlsOptions {
  /** Container holding one or more `[data-action]` buttons. */
  readonly root: HTMLElement;
  readonly controller: InputController;
  /** Called with whatever engine actions a press fired immediately. */
  readonly onFire?: (actions: readonly ControllerAction[]) => void;
  /** Injectable for tests; defaults to `window`. */
  readonly windowTarget?: EventTarget;
  /** Injectable for tests; defaults to `document`. */
  readonly documentTarget?: EventTarget;
}

export interface TouchControlsHandle {
  destroy(): void;
}

export function bindTouchControls(options: BindTouchControlsOptions): TouchControlsHandle {
  const { root, controller, onFire } = options;
  const windowTarget = options.windowTarget ?? window;
  const documentTarget = options.documentTarget ?? document;

  /** Which action each live pointer started, so a release only ever ends its own press. */
  const activePointers = new Map<number, ControllerAction>();

  function releaseByPointerId(event: Event): void {
    const pointerId = (event as PointerEvent).pointerId;
    const action = activePointers.get(pointerId);
    if (action === undefined) return;
    activePointers.delete(pointerId);
    controller.release(action);
  }

  function releaseEverything(): void {
    activePointers.clear();
    controller.releaseAll();
  }

  const teardown: Array<() => void> = [];

  for (const button of Array.from(root.querySelectorAll<HTMLElement>('[data-action]'))) {
    const action = button.dataset['action'];
    if (!isControllerAction(action)) continue;

    button.style.touchAction = 'none';

    const onPointerDown = (event: Event): void => {
      const pointerEvent = event as PointerEvent;
      event.preventDefault();
      try {
        button.setPointerCapture(pointerEvent.pointerId);
      } catch {
        // Pointer capture is unavailable in a handful of older WebViews; the
        // window-level catch-all below still guarantees a release.
      }
      activePointers.set(pointerEvent.pointerId, action);
      const fired = controller.press(action);
      if (fired.length > 0) onFire?.(fired);
    };

    button.addEventListener('pointerdown', onPointerDown);
    button.addEventListener('pointerup', releaseByPointerId);
    button.addEventListener('pointercancel', releaseByPointerId);
    button.addEventListener('lostpointercapture', releaseByPointerId);

    teardown.push(() => {
      button.removeEventListener('pointerdown', onPointerDown);
      button.removeEventListener('pointerup', releaseByPointerId);
      button.removeEventListener('pointercancel', releaseByPointerId);
      button.removeEventListener('lostpointercapture', releaseByPointerId);
    });
  }

  // The catch-all: a release that never reached its button (capture failed,
  // the finger left the viewport, or the tab lost focus entirely).
  windowTarget.addEventListener('pointerup', releaseByPointerId);
  windowTarget.addEventListener('pointercancel', releaseByPointerId);
  windowTarget.addEventListener('blur', releaseEverything);
  documentTarget.addEventListener('visibilitychange', releaseEverything);

  return {
    destroy(): void {
      for (const off of teardown) off();
      windowTarget.removeEventListener('pointerup', releaseByPointerId);
      windowTarget.removeEventListener('pointercancel', releaseByPointerId);
      windowTarget.removeEventListener('blur', releaseEverything);
      documentTarget.removeEventListener('visibilitychange', releaseEverything);
    },
  };
}
